import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Canvas from './Canvas';
import LeftSidebar from './LeftSidebar';
import PropertiesPanel from './PropertiesPanel';
import FramesBar from './FramesBar';
import { makeTextElement, makePhotoElement } from '../shared/elements';
import { listTemplates, loadTemplate, createTemplate, updateTemplate, uploadBackground } from '../shared/api';

let frameKeySeq = 0;
const nextFrameKey = () => {
  frameKeySeq += 1;
  return `frame-${Date.now()}-${frameKeySeq}`;
};

// Defaults tuned for the dual Twilio MMS + Stripo/Mailgun email delivery
// paths: 600×600 renders 1:1 at MMS width AND fits the 600px email content
// column, and the square aspect keeps subject placement flexible across both
// channels. See below-fold delivery tips in LeftSidebar.
const BLANK_TEMPLATE = () => ({
  id: null,
  name: 'Untitled Template',
  output_format: 'static',
  width: 600,
  height: 600,
  package_id: null,
  frames: [newFrame()],
});

function newFrame() {
  return {
    clientKey: nextFrameKey(),
    id: null,
    duration_ms: 800,
    elements: [],
    backgroundUrl: null,
    backgroundFile: null, // unsaved File object
  };
}

export default function TemplateEditor({ onDirtyChange }) {
  const [template, setTemplate] = useState(BLANK_TEMPLATE);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  // Report unsaved-changes state to the parent so it can trigger the same
  // navigation guards the FavoritesDesigner uses for its LookEditor.
  useEffect(() => { onDirtyChange && onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const refreshList = useCallback(() => {
    listTemplates().then(setSavedTemplates).catch((e) => console.error(e));
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  // Pricelist list for the PRICELIST selector — reuses the FavoritesDesigner's
  // packages endpoint so we don't invent a second source of truth.
  useEffect(() => {
    fetch('/admin/favorites_designer/packages_list', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPackages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const currentFrame = template.frames[currentFrameIndex] || template.frames[0];
  const elements = currentFrame?.elements || [];
  const selectedElement = elements.find((el) => el.id === selectedId) || null;

  const mutateFrame = (patch) => {
    setDirty(true);
    setTemplate((t) => {
      const frames = t.frames.slice();
      frames[currentFrameIndex] = { ...frames[currentFrameIndex], ...patch };
      return { ...t, frames };
    });
  };

  const mutateElements = (updater) => {
    mutateFrame({ elements: updater(currentFrame.elements) });
  };

  // ── template-level actions ──────────────────────────────────────────────
  const onNameChange = (name) => { setDirty(true); setTemplate((t) => ({ ...t, name })); };

  const onToggleGif = () => {
    setDirty(true);
    setTemplate((t) => ({ ...t, output_format: t.output_format === 'gif' ? 'static' : 'gif' }));
  };

  const onDimensionsChange = (patch) => {
    setDirty(true);
    setTemplate((t) => ({ ...t, ...patch }));
  };

  const onPackageChange = (packageId) => {
    setDirty(true);
    setTemplate((t) => ({ ...t, package_id: packageId }));
  };

  const onNew = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setTemplate(BLANK_TEMPLATE());
    setCurrentFrameIndex(0);
    setSelectedId(null);
    setDirty(false);
  };

  const onLoadTemplate = async (id) => {
    if (dirty && !window.confirm('You have unsaved changes. If you load a different template, your changes will be lost.')) return;
    try {
      const data = await loadTemplate(id);
      setTemplate({
        id: data.id,
        name: data.name,
        output_format: data.output_format,
        width: data.width,
        height: data.height,
        package_id: data.package_id ?? null,
        frames: (data.frames.length ? data.frames : [newFrame()]).map((f) => ({
          clientKey: nextFrameKey(),
          id: f.id || null,
          duration_ms: f.duration_ms || 800,
          elements: f.elements || [],
          backgroundUrl: f.background_url || null,
          backgroundFile: null,
        })),
      });
      setCurrentFrameIndex(0);
      setSelectedId(null);
      setDirty(false);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: template.name,
        output_format: template.output_format,
        width: template.width,
        height: template.height,
        package_id: template.package_id,
        frames: template.frames.map((f, i) => ({
          id: f.id,
          position: i,
          duration_ms: f.duration_ms,
          elements: f.elements,
        })),
      };
      const saved = template.id
        ? await updateTemplate(template.id, payload)
        : await createTemplate(payload);

      // Upload any pending backgrounds (in savedOrder, by position).
      const newFrames = template.frames.slice();
      for (let i = 0; i < newFrames.length; i += 1) {
        const local = newFrames[i];
        const persisted = saved.frames[i];
        if (persisted) newFrames[i] = { ...local, id: persisted.id, backgroundUrl: persisted.background_url || local.backgroundUrl };
        if (local.backgroundFile && persisted) {
          const result = await uploadBackground(saved.id, persisted.id, local.backgroundFile);
          newFrames[i] = { ...newFrames[i], backgroundUrl: result.background_url, backgroundFile: null };
        }
      }

      setTemplate((t) => ({ ...t, id: saved.id, frames: newFrames }));
      setDirty(false);
      refreshList();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── frame-level actions ────────────────────────────────────────────────
  const onUploadBackground = (file) => {
    const url = URL.createObjectURL(file);
    mutateFrame({ backgroundFile: file, backgroundUrl: url });
  };

  const onAddText  = () => { const el = makeTextElement();  mutateElements((els) => [...els, el]); setSelectedId(el.id); };
  const onAddPhoto = () => { const el = makePhotoElement(); mutateElements((els) => [...els, el]); setSelectedId(el.id); };

  const onChangeElement = (updated) => {
    mutateElements((els) => els.map((e) => (e.id === updated.id ? updated : e)));
  };
  const onDeleteElement = (id) => {
    mutateElements((els) => els.filter((e) => e.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const onAddFrame = () => {
    setDirty(true);
    setTemplate((t) => ({ ...t, frames: [...t.frames, newFrame()] }));
    setCurrentFrameIndex(template.frames.length);
    setSelectedId(null);
  };

  const onDeleteFrame = () => {
    if (template.frames.length <= 1) return;
    if (!window.confirm(`Delete Frame ${currentFrameIndex + 1}?`)) return;
    setDirty(true);
    setTemplate((t) => ({ ...t, frames: t.frames.filter((_, i) => i !== currentFrameIndex) }));
    setCurrentFrameIndex((i) => Math.max(0, i - 1));
    setSelectedId(null);
  };

  const onDurationChange = (ms) => mutateFrame({ duration_ms: ms });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '6px 16px', fontSize: 13 }}>{error}</div>
      )}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LeftSidebar
          template={template}
          savedTemplates={savedTemplates}
          onNameChange={onNameChange}
          onLoadTemplate={onLoadTemplate}
          onNew={onNew}
          onSave={onSave}
          onUploadBackground={onUploadBackground}
          onAddText={onAddText}
          onAddPhoto={onAddPhoto}
          onToggleGif={onToggleGif}
          onDimensionsChange={onDimensionsChange}
          onPackageChange={onPackageChange}
          packages={packages}
          elements={elements}
          selectedId={selectedId}
          onSelectElement={setSelectedId}
          onDeleteElement={onDeleteElement}
          currentFrame={currentFrame}
          saving={saving}
          dirty={dirty}
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Canvas
            width={template.width}
            height={template.height}
            backgroundUrl={currentFrame?.backgroundUrl}
            elements={elements}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onChangeElement={onChangeElement}
          />
          <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', padding: 6, background: '#1f2a36' }}>
            💡 Drag to move · Corner handle to resize · Click to select
          </div>
          <FramesBar
            frames={template.frames}
            currentFrameIndex={currentFrameIndex}
            onSelect={(i) => { setCurrentFrameIndex(i); setSelectedId(null); }}
            onAdd={onAddFrame}
            onDelete={onDeleteFrame}
            onDurationChange={onDurationChange}
            isGif={template.output_format === 'gif'}
          />
        </div>
        <PropertiesPanel
          element={selectedElement}
          onChange={onChangeElement}
          onDelete={() => selectedElement && onDeleteElement(selectedElement.id)}
        />
      </div>
    </div>
  );
}
